/**
 * Round icon for an instrument: a glyph on a coloured disc. BTC, ETH, SOL, DOGE, AAPL, TSLA and NVDA marks are from Simple Icons (CC0);
 * the rest are drawn here. Coins and companies without a drawn mark show their logo when the API holds one (stores/logos.ts). Forex
 * pairs show the base currency's flag with the quote's as a badge, and non-US indices their country's flag. Other symbols fall back to
 * a letter mark (index number, metal symbol or first letters) on their asset class's colour, or a hashed one. A logo an admin
 * uploaded (Admin → Instruments) is shown before any of these.
 */
import { useState, type ReactNode } from 'react';
import { assetClassOf, instrumentTag, pairParts, type AssetClass } from '@platform/shared';
import { useLogoUploaded, useLogoUrl } from '../stores/logos';
import { Flag } from './Flags';
import { cx } from './ui';

/** `coin`: the mark is itself a disc with a knocked-out letter, so it is drawn full size in `bg` on white. */
type Glyph = { bg: string; fill?: string[]; stroke?: boolean; coin?: boolean; evenodd?: boolean; paths: string[] };

/** 24×24 path data. */
const SI = {
  BTC: 'M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.548v-.002zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93h.01zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.524 2.75 2.084v.006z',
  ETH: 'M11.944 17.97L4.58 13.62 11.943 24l7.37-10.38-7.372 4.35h.003zM12.056 0L4.69 12.223l7.365 4.354 7.365-4.35L12.056 0z',
  SOL: 'm23.8764 18.0313-3.962 4.1393a.9201.9201 0 0 1-.306.2106.9407.9407 0 0 1-.367.0742H.4599a.4689.4689 0 0 1-.2522-.0733.4513.4513 0 0 1-.1696-.1962.4375.4375 0 0 1-.0314-.2545.4438.4438 0 0 1 .117-.2298l3.9649-4.1393a.92.92 0 0 1 .3052-.2102.9407.9407 0 0 1 .3658-.0746H23.54a.4692.4692 0 0 1 .2523.0734.4531.4531 0 0 1 .1697.196.438.438 0 0 1 .0313.2547.4442.4442 0 0 1-.1169.2297zm-3.962-8.3355a.9202.9202 0 0 0-.306-.2106.941.941 0 0 0-.367-.0742H.4599a.4687.4687 0 0 0-.2522.0734.4513.4513 0 0 0-.1696.1961.4376.4376 0 0 0-.0314.2546.444.444 0 0 0 .117.2297l3.9649 4.1394a.9204.9204 0 0 0 .3052.2102c.1154.049.24.0744.3658.0746H23.54a.469.469 0 0 0 .2523-.0734.453.453 0 0 0 .1697-.1961.4382.4382 0 0 0 .0313-.2546.4444.4444 0 0 0-.1169-.2297zM.46 6.7225h18.7815a.9411.9411 0 0 0 .367-.0742.9202.9202 0 0 0 .306-.2106l3.962-4.1394a.4442.4442 0 0 0 .117-.2297.4378.4378 0 0 0-.0314-.2546.453.453 0 0 0-.1697-.196.469.469 0 0 0-.2523-.0734H4.7596a.941.941 0 0 0-.3658.0745.9203.9203 0 0 0-.3052.2102L.1246 5.9687a.4438.4438 0 0 0-.1169.2295.4375.4375 0 0 0 .0312.2544.4512.4512 0 0 0 .1692.196.4689.4689 0 0 0 .2518.0739z',
  DOGE: 'M12.288 7.908h-1.715v3.38h2.697v1.415h-2.697v3.38h1.799c.462 0 3.794.052 3.789-3.933-.005-3.984-3.232-4.242-3.873-4.242zM12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.472 18.481H8.126v-5.778H6.594v-1.415h1.532V5.511h3.73c.882 0 6.727-.183 6.727 6.594-.001 6.888-6.111 6.376-6.111 6.376z',
  AAPL: 'M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701',
  TSLA: 'M12 5.362l2.475-3.026s4.245.09 8.471 2.054c-1.082 1.636-3.231 2.438-3.231 2.438-.146-1.439-1.154-1.79-4.354-1.79L12 24 8.619 5.034c-3.18 0-4.188.354-4.335 1.792 0 0-2.146-.795-3.229-2.43C5.28 2.431 9.525 2.34 9.525 2.34L12 5.362l-.004.002H12v-.002zm0-3.899c3.415-.03 7.326.528 11.328 2.28.535-.968.672-1.395.672-1.395C19.625.612 15.528.015 12 0 8.472.015 4.375.61 0 2.349c0 0 .195.525.672 1.396C4.674 1.989 8.585 1.435 12 1.46v.003z',
  NVDA: 'M8.948 8.798v-1.43a6.7 6.7 0 0 1 .424-.018c3.922-.124 6.493 3.374 6.493 3.374s-2.774 3.851-5.75 3.851c-.398 0-.787-.062-1.158-.185v-4.346c1.528.185 1.837.857 2.747 2.385l2.04-1.714s-1.492-1.952-4-1.952a6.016 6.016 0 0 0-.796.035m0-4.735v2.138l.424-.027c5.45-.185 9.01 4.47 9.01 4.47s-4.08 4.964-8.33 4.964c-.37 0-.733-.035-1.095-.097v1.325c.3.035.61.062.91.062 3.957 0 6.82-2.023 9.593-4.408.459.371 2.34 1.263 2.73 1.652-2.633 2.208-8.772 3.984-12.253 3.984-.335 0-.653-.018-.971-.053v1.864H24V4.063zm0 10.326v1.131c-3.657-.654-4.673-4.46-4.673-4.46s1.758-1.944 4.673-2.262v1.237H8.94c-1.528-.186-2.73 1.245-2.73 1.245s.68 2.412 2.739 3.11M2.456 10.9s2.164-3.197 6.5-3.533V6.201C4.153 6.59 0 10.653 0 10.653s2.35 6.802 8.948 7.42v-1.237c-4.84-.6-6.492-5.936-6.492-5.936z',
};

/** Three stacked bars, for metals. */
const BARS = 'M0 21.5 2.2 14.5h8.6l2.2 7zM11 21.5l2.2-7h8.6l2.2 7zM5.5 12.5 7.7 5.5h8.6l2.2 7z';

const GLYPHS: Record<string, Glyph> = {
  BTC: { bg: '#f7931a', coin: true, paths: [SI.BTC] },
  ETH: { bg: '#627eea', paths: [SI.ETH] },
  SOL: { bg: '#9945ff', paths: [SI.SOL] },
  XRP: { bg: '#23292f', stroke: true, paths: ['M4.5 5l4.7 4.6a4 4 0 0 0 5.6 0L19.5 5', 'M4.5 19l4.7-4.6a4 4 0 0 1 5.6 0l4.7 4.6'] },
  DOGE: { bg: '#c2a633', coin: true, paths: [SI.DOGE] },
  GOLD: { bg: '#c9a227', paths: [BARS] },
  SILVER: { bg: '#8c96a3', evenodd: true, paths: ['M12 3a9 9 0 1 0 0 18 9 9 0 1 0 0-18zm0 2.5a6.5 6.5 0 1 1 0 13 6.5 6.5 0 1 1 0-13zM12 8a4 4 0 1 0 0 8 4 4 0 1 0 0-8z'] },
  OIL: { bg: '#3b3b3b', paths: ['M12 2.5S5 10.4 5 15a7 7 0 0 0 14 0c0-4.6-7-12.5-7-12.5z'] },
  NATGAS: { bg: '#2f80ed', paths: ['M12 2c.6 3.4-1.9 5.1-3.6 7.3C6.9 11.2 6 13 6 15a6 6 0 0 0 12 0c0-2.6-1.3-4.6-2.7-6.2-.2 1.6-1 2.7-2.1 3.2.6-3.4-.2-7.2-1.2-10z'] },
  AAPL: { bg: '#555b66', paths: [SI.AAPL] },
  TSLA: { bg: '#cc0000', paths: [SI.TSLA] },
  NVDA: { bg: '#5e9a00', paths: [SI.NVDA] },
  AMZN: { bg: '#e68a00', stroke: true, paths: ['M3.5 12.5c4.8 3.6 12.2 3.6 17 0', 'M16.8 11.4l3.7 1.1-1 3.6'] },
  MSFT: {
    bg: '#5e5e5e',
    fill: ['#f25022', '#7fba00', '#00a4ef', '#ffb900'],
    paths: ['M3 3h8.5v8.5H3z', 'M12.5 3H21v8.5h-8.5z', 'M3 12.5h8.5V21H3z', 'M12.5 12.5H21V21h-8.5z'],
  },
  SPX500: { bg: '#1d4ed8', stroke: true, paths: ['M3 18l5.5-5.5 4 3.5L20 8.5', 'M15 8.5h5v5'] },
  BRENT: { bg: '#5b4636', paths: ['M12 2.5S5 10.4 5 15a7 7 0 0 0 14 0c0-4.6-7-12.5-7-12.5z'] },
  PLATINUM: { bg: '#6f7f91', paths: [BARS] },
  PALLADIUM: { bg: '#8b8580', paths: [BARS] },
  COPPER: { bg: '#b0602f', paths: [BARS] },
  CORN: { bg: '#c99a12', paths: ['M12 2c2.8 0 4.6 4.5 4.6 10S14.8 22 12 22s-4.6-4.5-4.6-10S9.2 2 12 2z'] },
  WHEAT: {
    bg: '#b8862b',
    stroke: true,
    paths: ['M12 22V5', 'M12 10C9.5 10 8 8.5 8 6c2.5 0 4 1.5 4 4', 'M12 10c2.5 0 4-1.5 4-4-2.5 0-4 1.5-4 4', 'M12 15c-2.5 0-4-1.5-4-4 2.5 0 4 1.5 4 4', 'M12 15c2.5 0 4-1.5 4-4-2.5 0-4 1.5-4 4'],
  },
  SOYBEAN: { bg: '#7a8b2f', paths: ['M5 16.5C5 10 9.7 5 16.5 5c1 0 2.5.3 2.5.3s.6 13.7-11.6 13.7C6 19 5 18 5 16.5z'] },
  SUGAR: { bg: '#4f7cac', stroke: true, paths: ['M12 3l8 4.5v9L12 21l-8-4.5v-9z', 'M4 7.5l8 4.5 8-4.5', 'M12 12v9'] },
};

/** Flag for a non-US index: its currency's flag, or its country's where the currency is shared (euro) or not drawn. */
const INDEX_FLAG: Record<string, string> = {
  GER40: 'DE',
  UK100: 'GBP',
  FRA40: 'FR',
  EU50: 'EUR',
  JP225: 'JPY',
  HK50: 'HKD',
  AUS200: 'AUD',
  CHINA50: 'CNH',
  NL25: 'NL',
  SWI20: 'CHF',
  ESP35: 'ES',
  SG30: 'SGD',
  TAIWAN: 'TW',
  IN50: 'IN',
};

function hashColor(s: string) {
  let h = 0;
  for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return `hsl(${h} 55% 45%)`;
}

/** Chemical symbol for metals without a drawn glyph, from their market code (XPT/USD → Pt). */
const ELEMENT: Record<string, string> = { XPT: 'Pt', XPD: 'Pd', XCU: 'Cu', XAU: 'Au', XAG: 'Ag' };

/** Fallback disc colour by asset class, so a market without its own mark still reads as its kind. */
const CLASS_BG: Partial<Record<AssetClass, string>> = { COMMODITY: '#a77b1c', INDEX: '#1d4ed8' };

/** Fallback letters: the index number (NAS100 → 100), a metal's chemical symbol, else the first three letters. */
function fallbackText(symbol: string, cls: AssetClass | undefined) {
  if (cls === 'INDEX') return symbol.match(/\d+$/)?.[0] ?? symbol.slice(0, 3);
  const el = ELEMENT[instrumentTag(symbol)?.slice(0, 3) ?? ''];
  return el ?? symbol.slice(0, 3);
}

/** Forex pair: the base currency's flag, with the quote currency's flag as a small badge at the bottom right. */
function PairFlags({ base, quote, size }: { base: string; quote: string; size: number }) {
  return (
    <span aria-hidden="true" className="relative shrink-0" style={{ width: size, height: size }}>
      <Flag ccy={base} size={Math.round(size * 0.9)} className="absolute top-0 left-0" />
      <Flag ccy={quote} size={Math.round(size * 0.48)} className="absolute right-0 bottom-0 ring-2 ring-panel" />
    </span>
  );
}

/** A coin or company logo in a white disc; a stock's square logo gets a margin so its corners aren't cut. */
function Logo({ src, size, padded, onError }: { src: string; size: number; padded: boolean; onError: () => void }) {
  const inner = padded ? Math.round(size * 0.7) : size;
  return (
    <span aria-hidden="true" className="grid shrink-0 place-items-center overflow-hidden rounded-full bg-white ring-1 ring-black/5" style={{ width: size, height: size }}>
      <img src={src} alt="" width={inner} height={inner} loading="lazy" decoding="async" onError={onError} className="object-contain" style={{ width: inner, height: inner }} />
    </span>
  );
}

export function InstrumentIcon({ symbol, size = 32 }: { symbol: string; size?: number }) {
  const cls = assetClassOf(symbol);
  const logo = useLogoUrl(symbol);
  const uploaded = useLogoUploaded(symbol);
  const [broken, setBroken] = useState<string | null>(null);
  // A logo uploaded in Admin → Instruments wins over everything drawn here.
  if (uploaded && logo && broken !== logo) return <Logo src={logo} size={size} padded={cls === 'STOCK'} onError={() => setBroken(logo)} />;
  if (cls === 'FX') {
    const [base, quote] = pairParts(symbol, cls);
    if (quote) return <PairFlags base={base} quote={quote} size={size} />;
  }
  if (cls === 'INDEX' && INDEX_FLAG[symbol]) {
    return (
      <span aria-hidden="true" className="shrink-0" style={{ width: size, height: size }}>
        <Flag ccy={INDEX_FLAG[symbol]} size={size} />
      </span>
    );
  }
  const g = GLYPHS[symbol];
  if (!g && logo && broken !== logo) return <Logo src={logo} size={size} padded={cls === 'STOCK'} onError={() => setBroken(logo)} />;
  const box = Math.round(size * (g?.coin ? 1 : 0.58));
  const text = g ? '' : fallbackText(symbol, cls);
  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-full font-bold text-white"
      style={{
        width: size,
        height: size,
        background: g?.coin ? '#fff' : (g?.bg ?? (cls && CLASS_BG[cls]) ?? hashColor(symbol)),
        fontSize: Math.round(size * (text.length > 2 ? 0.3 : 0.36)),
      }}
    >
      {g ? (
        <svg viewBox="0 0 24 24" width={box} height={box}>
          {g.paths.map((d, i) =>
            g.stroke ? (
              <path key={i} d={d} fill="none" stroke="#fff" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" />
            ) : (
              <path key={i} d={d} fill={g.fill?.[i] ?? (g.coin ? g.bg : '#fff')} fillRule={g.evenodd ? 'evenodd' : undefined} />
            ),
          )}
        </svg>
      ) : (
        text
      )}
    </span>
  );
}

/** An instrument's icon followed by its symbol (or `children`), as staff tables and pop-ups show it. */
export function InstrumentLabel({ symbol, size = 18, className, children }: { symbol: string; size?: number; className?: string; children?: ReactNode }) {
  return (
    <span className={cx('inline-flex items-center gap-2 align-middle whitespace-nowrap', className)}>
      <InstrumentIcon symbol={symbol} size={size} />
      <span>{children ?? symbol}</span>
    </span>
  );
}
